import { AFFILIATE, AFFONSO_AFFILIATE, LEMON_AFFILIATE, normalizeAffiliateCode, normalizeAffiliateSource, normalizeLemonAffiliateRef } from '~/constants/affiliate'

/**
 * Affiliate links: https://inmyresume.com/any-page?ref=code(&src=placement).
 *
 * The click always goes to /api/affiliate/click, which counts it for the
 * partner's statistics (no personal data, nothing stored in the browser). The
 * referral cookie that later credits a sign-up or purchase is set only with
 * the visitor's cookie consent: right away when they have already accepted,
 * or when they accept later in this visit. Declining removes it again. The
 * parameters are dropped from the address bar so they are not shared further
 * by accident.
 *
 * LemonSqueezy affiliate links (?aff=…) are tracked by their own script; here
 * only the time of the click is remembered, with the same consent, so the
 * checkout can credit whichever program's link came last. Their parameter
 * stays in the address bar for that script to read.
 *
 * Affonso links are tracked by Affonso's pixel (nuxt.config.ts) in consent
 * mode: it sets its `affonso_referral` cookie only once affonsoConsentGranted()
 * is called here, and it needs the `affonso_id` it put in the address bar at
 * that moment, so an id that a navigation dropped is put back first. Each
 * Affonso click seen with consent is dated for the checkout, like
 * LemonSqueezy's; declining removes the pixel's cookies.
 */

interface AffonsoWindow {
  affonsoConsentGranted?: () => Promise<boolean>
  affonso_referral?: string
}

/** Detail of the pixel's `affonso_referral_ready` event. */
interface AffonsoReferralReady {
  referralId?: string
  /** Present only after a click was tracked, not when an old cookie was read. */
  consentMode?: boolean
}

/** The pixel loads asynchronously; its consent hook appears once it has. */
function whenAffonsoReady(timeoutMs = 15000): Promise<AffonsoWindow | null> {
  const w = window as unknown as AffonsoWindow
  const started = Date.now()
  return new Promise((resolve) => {
    const check = () => {
      if (typeof w.affonsoConsentGranted === 'function') return resolve(w)
      if (Date.now() - started > timeoutMs) return resolve(null)
      setTimeout(check, 250)
    }
    check()
  })
}

/** Removes the pixel's cookies, which it sets on the site's root domain. */
function forgetAffonso(): void {
  const root = location.hostname.replace(/^www\./, '')
  for (const name of [AFFONSO_AFFILIATE.referralCookie, AFFONSO_AFFILIATE.dataCookie]) {
    for (const domain of ['', `; domain=${root}`, `; domain=.${root}`]) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}`
    }
  }
  ;(window as unknown as AffonsoWindow).affonso_referral = ''
}

export default defineNuxtPlugin(() => {
  const router = useRouter()
  const user = useSupabaseUser()

  /** A code seen before consent, kept in memory only until the visitor decides. */
  let pendingCode: string | null = null
  /** Same for a LemonSqueezy affiliate click. */
  let pendingLemonClick = false
  /** The ?aff value already handled: it stays in the URL, and later route changes must not re-date the click. */
  let seenLemonRef: string | null = null

  /** The pixel's click id from before consent; its cookie needs it in the URL. */
  let pendingAffonsoId: string | null = null

  const first = (value: unknown) => Array.isArray(value) ? value[0] : value

  const grantAffonso = async () => {
    const affonso = await whenAffonsoReady()
    if (!affonso || !hasCookieConsent()) return
    const url = new URL(location.href)
    if (pendingAffonsoId && !url.searchParams.has('affonso_id')) {
      url.searchParams.set('affonso_id', pendingAffonsoId)
      history.replaceState(history.state, '', url.toString())
    }
    pendingAffonsoId = null
    await affonso.affonsoConsentGranted!().catch(() => false)
  }

  // Fired by the pixel after it tracked a click on an Affonso link, before
  // consent too (then without a cookie, which grantAffonso later upgrades).
  window.addEventListener('affonso_referral_ready', (event) => {
    const detail = (event as CustomEvent<AffonsoReferralReady>).detail
    if (!detail || !('consentMode' in detail) || !detail.referralId) return
    if (hasCookieConsent()) {
      $fetch('/api/affiliate/affonso-click', { method: 'POST' }).catch(() => {})
    } else {
      pendingAffonsoId = detail.referralId
    }
  })

  const captureLemon = (query: Record<string, unknown>) => {
    const ref = normalizeLemonAffiliateRef(first(query[LEMON_AFFILIATE.queryParam]))
    if (!ref || ref === seenLemonRef) return
    seenLemonRef = ref
    if (hasCookieConsent()) {
      $fetch('/api/affiliate/lemon-click', { method: 'POST' }).catch(() => {})
    } else {
      pendingLemonClick = true
    }
  }

  const capture = async (query: Record<string, unknown>) => {
    captureLemon(query)
    const code = normalizeAffiliateCode(first(query[AFFILIATE.queryParam]))
    if (!code) return

    const source = normalizeAffiliateSource(first(query.src))
    const remember = hasCookieConsent()
    await $fetch('/api/affiliate/click', { method: 'POST', body: { code, source, remember } }).catch(() => {})
    pendingCode = remember ? null : code

    const { [AFFILIATE.queryParam]: _ref, src: _src, ...rest } = query
    router.replace({ query: rest as Record<string, string> }).catch(() => {})

    // Clicked while already signed in: link the account right away.
    if (remember && user.value) attach(true)
  }

  // Links a newly signed-in account to the partner whose link brought it, so
  // the sign-up shows in their statistics. Once per browser session is enough;
  // the server ignores accounts that are already linked or had no click.
  const ATTACHED_KEY = 'imr_ref_attached'
  const attach = (force = false) => {
    try {
      if (!force && sessionStorage.getItem(ATTACHED_KEY) === user.value?.id) return
      sessionStorage.setItem(ATTACHED_KEY, user.value?.id ?? '')
    } catch {
      // Storage blocked: attaching twice is harmless.
    }
    $fetch('/api/affiliate/attach', { method: 'POST' }).catch(() => {})
  }

  window.addEventListener(COOKIE_CONSENT_EVENT, async (event) => {
    const granted = (event as CustomEvent<{ granted: boolean }>).detail?.granted
    if (!granted) {
      pendingCode = null
      pendingLemonClick = false
      pendingAffonsoId = null
      forgetAffonso()
      $fetch('/api/affiliate/forget', { method: 'POST' }).catch(() => {})
      return
    }
    grantAffonso()
    if (pendingLemonClick) {
      pendingLemonClick = false
      await $fetch('/api/affiliate/lemon-click', { method: 'POST' }).catch(() => {})
    }
    if (!pendingCode) return
    const code = pendingCode
    pendingCode = null
    await $fetch('/api/affiliate/remember', { method: 'POST', body: { code } }).catch(() => {})
    if (user.value) attach(true)
  })

  if (hasCookieConsent()) grantAffonso()
  router.isReady().then(() => capture(router.currentRoute.value.query))
  router.afterEach(to => capture(to.query))
  watch(() => user.value?.id, (id) => {
    if (id) attach()
  }, { immediate: true })
})
